// ABOUTME: Sticky footer component containing action buttons for adding workouts and meals
// ABOUTME: Positioned at bottom of screen with backdrop blur and elevated styling

import React, { useState, useRef } from 'react';
import { StorageManager } from '../services/storage/StorageManager';
import { exportData, ExportFormat } from '../services/storage/exportUtils';
import { importData, ImportFormat, ImportResult } from '../services/storage/importUtils';

interface StickyFooterProps {
    onOpenWorkoutModal: () => void;
    onOpenMealModal: () => void;
    onOpenSettings: () => void;
    onDataSeeded: () => void;
}

const StickyFooter: React.FC<StickyFooterProps> = ({ 
    onOpenWorkoutModal, 
    onOpenMealModal, 
    onOpenSettings,
    onDataSeeded
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState<ExportFormat>('json');
    const [isImporting, setIsImporting] = useState(false);
    const [importFormat, setImportFormat] = useState<ImportFormat>('json');
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleExport = async () => {
        setIsExporting(true);
        setErrorMessage(null);
        try {
            const provider = await StorageManager.getProvider();
            await exportData(provider, exportFormat);
        } catch (error) {
            console.error('Failed to export data:', error);
            setErrorMessage(`Failed to export data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsImporting(true);
        setErrorMessage(null);
        setImportResult(null);

        try {
            const provider = await StorageManager.getProvider();
            const result = await importData(provider, file, importFormat, {
                skipDuplicates: true,
                updateExisting: false,
                validateData: true
            });
            
            setImportResult(result);
            
            if (result.imported > 0) {
                onDataSeeded(); // Refresh the data display
            }
            
            if (result.errors.length > 0) {
                setErrorMessage(`Import completed with errors: ${result.errors.slice(0, 2).join('; ')}${result.errors.length > 2 ? '...' : ''}`);
            }
        } catch (error) {
            console.error('Failed to import data:', error);
            setErrorMessage(`Failed to import data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsImporting(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const scrollContainerStyle = {
        overflowX: 'auto' as const,
        scrollbarWidth: 'none' as const,
        msOverflowStyle: 'none' as const,
        WebkitOverflowScrolling: 'touch' as const,
    };

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-md border-t border-slate-700 shadow-2xl">
            <div 
                style={scrollContainerStyle}
                className="scrollbar-hide"
            >
                <div className="flex items-center space-x-3 px-4 py-2" style={{ minWidth: 'max-content' }}>
                    {/* Add Workout Button */}
                    <button
                        onClick={onOpenWorkoutModal}
                        className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 transition-all duration-200 flex-shrink-0"
                    >
                        +Workout
                    </button>

                    {/* Add Meal Button */}
                    <button
                        onClick={onOpenMealModal}
                        className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-medium shadow-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition-all duration-200 flex-shrink-0"
                    >
                        +Meal
                    </button>

                    {/* Settings Button */}
                    <button
                        onClick={onOpenSettings}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 flex-shrink-0"
                        title="Settings"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>

                    {/* Separator */}
                    <div className="h-6 w-px bg-slate-600 flex-shrink-0"></div>

                    {/* Export Controls */}
                    <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                        disabled={isExporting || isImporting}
                        className="px-1.5 py-0.5 bg-slate-700 text-slate-200 border border-slate-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 text-xs flex-shrink-0"
                    >
                        <option value="json">JSON</option>
                        <option value="csv">CSV</option>
                        <option value="sqlite">SQLite</option>
                    </select>
                    
                    <button
                        onClick={handleExport}
                        disabled={isExporting || isImporting}
                        className="px-2 py-0.5 bg-gradient-to-r from-green-600 to-emerald-500 text-white font-medium shadow hover:from-green-700 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1 text-xs flex-shrink-0"
                    >
                        {isExporting ? (
                            <>
                                <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Exporting...
                            </>
                        ) : (
                            <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Export
                            </>
                        )}
                    </button>

                    {/* Import Controls */}
                    <select
                        value={importFormat}
                        onChange={(e) => setImportFormat(e.target.value as ImportFormat)}
                        disabled={isExporting || isImporting}
                        className="px-1.5 py-0.5 bg-slate-700 text-slate-200 border border-slate-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-xs flex-shrink-0"
                    >
                        <option value="json">JSON</option>
                        <option value="csv">CSV</option>
                        <option value="sqlite" disabled>SQLite</option>
                    </select>
                    
                    <button
                        onClick={handleImportClick}
                        disabled={isExporting || isImporting}
                        className="px-2 py-0.5 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-medium shadow hover:from-purple-700 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1 text-xs flex-shrink-0"
                    >
                        {isImporting ? (
                            <>
                                <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Importing...
                            </>
                        ) : (
                            <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                </svg>
                                Import
                            </>
                        )}
                    </button>
                    
                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,.csv,.sqlite,.sqlite3,.db"
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    {/* Status Messages */}
                    {(errorMessage || importResult) && (
                        <>
                            {/* Separator */}
                            <div className="h-6 w-px bg-slate-600 flex-shrink-0"></div>
                            
                            <div className="flex-shrink-0">
                                {errorMessage && (
                                    <div className="text-red-400 text-xs">
                                        {errorMessage}
                                    </div>
                                )}
                                {importResult && (
                                    <div className="text-slate-300 text-xs">
                                        Import: <span className="text-green-400">{importResult.imported} imported</span>
                                        {importResult.skipped > 0 && <span className="text-yellow-400">, {importResult.skipped} skipped</span>}
                                        {importResult.errors.length > 0 && <span className="text-red-400">, {importResult.errors.length} errors</span>}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StickyFooter;